<script>
import StringUtils from '@/utils/stringUtils'
import InputUtils from '@/utils/inputUtils'
export default
{
    name: 'pincode-input',

    props:
    {
        length: { type: Number, default: 4 },
        digitsPerRow: { type: Number, default: null },
        allowNumerals: { type: Boolean, default: true },
        allowAlphabeticals: { type: Boolean },
        allowSpecialCharacters: { type: Boolean },
        uppercaseOnly: { type: Boolean },
        autofocus: { type: Boolean },

        // bound to v-model.
        value: { type: String, default: '' },
    },

    created ()
    {
        for (let i = 0; i < this.length; i++)
        {
            this.digitInputs.push(<input class='digit' placeholder='•' type={this.allowAlphabeticals ? 'text' : 'tel'}
                on-input={this.onDigitInput} on-keydown={this.onDigitInputKeyDown} on-focus={this.onDigitInputFocused} />)
        }
    },

    render () 
    {
        return (
            <div class='pincode-input'>
                <div class='digits'>
                    {this.digitInputs}
                </div>
                <div v-show={!StringUtils.isEmptyString(this.errorMessage)} class='error-message'>{this.errorMessage}</div>
            </div>
        )
    },

    mounted ()
    {
        this.observer = new IntersectionObserver((mutations, observer) =>
        {
            this.resizeDigitInputs()
            setTimeout(this.resizeDigitInputs, 50)
            setTimeout(this.resizeDigitInputs, 1000)
        })
        this.observer.observe(this.$el, { childList: true, subtree: true })
        if (this.autofocus) this.digitInputsElems[0].focus()
    },

    data () 
    {
        return {
            digitInputs: [],
            errorMessage: '',
            observer: null
        }
    },

    computed:
    {
        digitInputsElems ()
        {
            return this.digitInputs.map(i => i.elm || i)
        },

        pinValue:
        {
            cache: false,
            get ()
            {
                return this.digitInputsElems.map(i => i.value).join(StringUtils.EMPTY)
            }
        }
    },

    watch:
    {
        digitInputs ()
        {
            this.$nextTick(this.resizeDigitInputs)
        },

        errorMessage ()
        {
            if (!StringUtils.isEmptyString(this.errorMessage))
            {
                this.$el.classList.add('has-error')
            }
            else this.$el.classList.remove('has-error')
        }
    },

    beforeDestroy ()
    {
        this.observer.disconnect()
    },

    methods:
    {
        onDigitInput (event)
        {
            const formatted = this.formatInputValue(event.target.value)
            if (!formatted) this.setDigitInputCharacter(StringUtils.EMPTY, event.target)

            const inputIdx = this.digitInputsElems.indexOf(event.target)
            for (let i = 0; i < formatted.length; i++)
            {
                const char = formatted[i]
                const input = this.digitInputsElems[inputIdx + i]
                if (!char || !input) break

                this.setDigitInputCharacter(char, input)
            }
            
            this.$emit('input', this.pinValue)            
        },

        setDigitInputCharacter (char, input)
        {
            const inputIdx = this.digitInputsElems.indexOf(input)

            input.value = char

            if (!StringUtils.isEmptyString(input.value))
            {
                if (inputIdx < this.length - 1)
                {
                    this.digitInputsElems[inputIdx + 1].focus()
                }
                if (inputIdx == this.digitInputsElems.length - 1)
                {
                    const emptyDigit = this.digitInputsElems.find(i => StringUtils.isEmptyString(i.value))
                    if (emptyDigit) return emptyDigit.focus()

                    this.$emit('input', this.pinValue)
                    this.$emit('pin-complete', this.pinValue)

                    return input.blur()
                }
            }
        },

        onDigitInputKeyDown (event)
        {
            const inputIdx = this.digitInputsElems.indexOf(event.target)

            if (event.keyCode == InputUtils.KEYCODES.BACKSPACE)
            {
                if (StringUtils.isEmptyString(event.target.value))
                {
                    if (inputIdx != 0) this.digitInputsElems[inputIdx - 1].focus()
                }
            }
        },

        onDigitInputFocused (event)
        {
            event.target.scrollIntoView()
            this.errorMessage = StringUtils.EMPTY
            event.target.setSelectionRange(0, event.target.value.length)
        },

        formatInputValue (value)
        {
            var formatted = ''
            
            for (let char of value)
            {
                const isAlphabetical = StringUtils.ALPHABET.toLowerCase().includes(char.toLowerCase())
                const isNumeral = StringUtils.NUMERALS.includes(char)

                if (this.uppercaseOnly) char = char.toString().toUpperCase()
                if (!this.allowSpecialCharacters)
                {
                    if (!isAlphabetical && !isNumeral)
                    {
                        char = StringUtils.EMPTY
                    }
                }
                if (!this.allowNumerals && isNumeral)
                {
                    char = StringUtils.EMPTY
                }
                if (!this.allowAlphabeticals && isAlphabetical)
                {
                    char = StringUtils.EMPTY
                }

                formatted += char
            }

            return formatted
        },
        
        resizeDigitInputs ()
        {
            const length = this.digitsPerRow  || this.length
            const digitStyle = window.getComputedStyle(this.digitInputsElems[0])
            for (let digit of this.digitInputsElems)
            {
                digit.style.width = `calc(100% / ${length} - ${digitStyle.margin} * 4)`
                const rect = digit.getBoundingClientRect()
                digit.style.height = rect.width * 1.2 + 'px'
                digit.style.fontSize = rect.width * 0.8 + 'px'
            }
        },

        setErrorMessage (message)
        {
            this.errorMessage = message
        }
    },

    components:
    {
    }
}
</script>

<style lang='less'>
.pincode-input
{
    color: var(--theme-text-color);

    .digits
    {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
        color: inherit;

        .digit
        {
            text-align: center;
            height: 100px;
            margin: 5px;
            padding: 5px;
            border-bottom: 2px solid var(--theme-border-color);
            border-radius: 0;
            background-color: transparent;
            color: inherit;

            &:hover
            {
                border-color: var(--theme-border-color-extra-strong);
            }

            &:focus
            {
                border-color: var(--theme-accent-color-text);
            }

            &::placeholder
            {
                color: var(--theme-border-color-strong);
            }
        }
    }

    .error-message
    {
        color: red;
        margin-top: 5px;
    }

    &.has-error
    {
        .digits
        {
            .digit
            {
                border-color: red;
            }
        }
    }
}
</style>


// WEBPACK FOOTER //
// src/components/PincodeInput.vue